import {
  DeleteOutlined,
  EditOutlined,
  FormatAlignCenterOutlined,
  FormatAlignLeftOutlined,
  FormatAlignRightOutlined,
  VerticalAlignBottomOutlined,
  VerticalAlignCenterOutlined,
  VerticalAlignTopOutlined,
} from '@vindr/icons';
import { useGetImageUrl } from '@vindr/services/actions/element.action';
import { getURLUploadImageElement, uploadImageElement } from '@vindr/services/apis/element.api';
import { Button, Checkbox, Form, Modal, Radio, RadioChangeEvent, Space, Tooltip } from 'antd';
import { RcFile } from 'antd/es/upload';
import {
  Dispatch,
  Fragment,
  SetStateAction,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import ImageElement from './ImageElement';
import { uuidv4 } from '@vindr/utils/helpers';
import { UploadBox } from '@vindr/ui';
import MedicalImageEdit from '../../../../../ui/MedicalImageEdit';
import { base64ToFile } from '@vindr/utils/converter';
import useRefreshComponent from '@vindr/utils/hooks/useRefreshComponent';

export interface ImageInputProps {
  disabled: boolean;
  element: ImageElement;
  onChange: (val, element: ImageElement, preventHighlight?: boolean) => void;
  onChangeValue: any;
  index?: number;
}

interface ImageModifyState {
  imageIndex: number;
  image: any;
  modalOpen: boolean;
}

type State = Pick<ImageElement, 'images' | 'align' | 'justify' | 'direction'>;

const ImageInput = ({ disabled, element, onChange, index }: ImageInputProps) => {
  const { t } = useTranslation('Vindoc');
  const { data: imageUrlsData } = useGetImageUrl({ ids: element.image_ids });
  const imageUrls = imageUrlsData?.data;
  const [defaultImage, setDefaultImage] = useState(null);
  const [state, setState] = useState<State>({
    images: element.images,
    align: element.align,
    justify: element.justify,
    direction: element.direction,
  });

  const [isHidden, setIsHidden] = useState(!!element.is_hidden);
  const [refresh, setRefresh] = useRefreshComponent();

  useEffect(() => {
    element.setIsHidden(isHidden);
    onChange(element.value, element);
    setRefresh();
  }, [element, isHidden, onChange, setRefresh]);

  useEffect(() => {
    if (element.default_image && element.default_image.id !== defaultImage?.id)
      setDefaultImage(element.default_image);
    if (!imageUrls) return;

    const imagesMap = new Map<string, typeof element.images[0]>();

    (element.images || []).forEach((img) => {
      if (img.id) imagesMap.set(img.id, img);
    });

    if (
      element.image_ids?.length === element?.images?.length &&
      element.images.every((img) => img.id && img.src)
    ) {
      setState((prev) => ({ ...prev, images: element.images }));
      return;
    }

    const images = [];
    for (const imageUrl of imageUrls) {
      const savedImage = imagesMap.get(imageUrl.id);
      const image = {
        ...savedImage,
        id: imageUrl.id,
        src: imageUrl.url,
        width: savedImage?.width || element.default_width,
        height: savedImage?.height || element.default_height,
        ratio: savedImage?.ratio || element.default_width / element.default_height,
        manualAdjustment: savedImage?.manualAdjustment ?? true,
      };
      images.push(image);
    }
    if (!element.default_image) {
      const defaultImage = images.find((img) => img.id === element.default_value);
      if (defaultImage) setDefaultImage(defaultImage);
    }
    setState((prev) => ({ ...prev, images }));
    onChange({ images }, element, true);
  }, [imageUrls, element, defaultImage]);

  const [imageModifyState, setImageModifyState] = useState<ImageModifyState>({
    imageIndex: -1,
    image: null,
    modalOpen: false,
  });

  const showEditModal = useCallback(
    (index: number) => {
      setImageModifyState({
        imageIndex: index,
        image: state.images[index],
        modalOpen: true,
      });
    },
    [state.images]
  );

  const handleCancel = useCallback(() => {
    setImageModifyState({
      imageIndex: -1,
      image: null,
      modalOpen: false,
    });
  }, []);

  const handleJustifyChange = useCallback(
    (e) => {
      setState((prev) => ({ ...prev, justify: e.target.value }));
      onChange({ justify: e.target.value }, element);
    },
    [element]
  );

  const handleAlignChange = useCallback(
    (e) => {
      setState((prev) => ({ ...prev, align: e.target.value }));
      onChange({ align: e.target.value }, element);
    },
    [element]
  );

  const handleDirectionChange = useCallback(
    (e) => {
      setState((prev) => ({ ...prev, direction: e.target.value }));
      onChange({ direction: e.target.value }, element);
    },
    [element]
  );

  const uploadImage = async (image) => {
    const img = await loadImage(image.src);
    const ratio = img.width / img.height;
    const file = base64ToFile(image.src, uuidv4() + '.png');

    // Step 3: Get presigned upload URL
    const response = await getURLUploadImageElement({
      action: 0,
      file_names: [uuidv4() + '.png'],
    });

    const presignedData = response?.data?.presigned_objects?.[0];
    if (!presignedData || !presignedData.url || !presignedData.id) {
      throw new Error('Invalid upload URL response');
    }

    // Step 4: Upload image to the presigned URL
    await uploadImageElement(presignedData.url, file);

    // Step 5: Construct the ImageData object
    return {
      id: presignedData.id,
      src: image.src,
      width: image.width,
      height: image.height,
      ratio,
      manualAdjustment: true,
    };
  };

  const handleSaveACopy = async (image) => {
    const newImage = await uploadImage(image);

    setState((prev) => {
      const updatedImages = [...prev.images, newImage];
      onChange({ images: updatedImages }, element);
      return { ...prev, images: updatedImages };
    });
    setImageModifyState({
      imageIndex: -1,
      image: null,
      modalOpen: false,
    });
  };

  const handleSave = async (image, imageProps) => {
    const imageUrl = imageUrls.find((url) => url.id === imageProps.id);
    const newImage = await uploadImage(image);

    setState((prev) => {
      const updatedImages = prev.images.map((img) => (img.id === imageUrl.id ? newImage : img));
      onChange({ images: updatedImages }, element);
      return { ...prev, images: updatedImages };
    });
    setImageModifyState({
      imageIndex: -1,
      image: null,
      modalOpen: false,
    });
  };

  const handleAddDefaultImage = async () => {
    if (!defaultImage) return;
    setState((prev) => {
      const updatedImages = [...prev.images, defaultImage];
      onChange({ images: updatedImages }, element);
      return { ...prev, images: updatedImages };
    });
  };

  const hasDefaultImage = useMemo(
    () => !!state.images.find((it) => it?.id === defaultImage?.id),
    [defaultImage, state?.images]
  );

  return (
    <Fragment key={refresh}>
      <div className="flex items-start">
        <Checkbox
          checked={!isHidden}
          className="mr-2 mt-0.5"
          onChange={(e) => setIsHidden(!e.target.checked)}
        />
        <div className="flex flex-1 justify-between">
          {element?.label ? (
            <Form.Item
              className="mb-0"
              label={[element?.name, index ?? ''].join(' ')}
              {...(element?.tooltip && { tooltip: { title: element?.tooltip } })}
            />
          ) : (
            <div></div>
          )}
          {!hasDefaultImage && (
            <Button size="small" type="default" onClick={handleAddDefaultImage}>
              {t('Add default image')}
            </Button>
          )}
        </div>
      </div>
      <Form.Item key={element.id} noStyle name={element.name}>
        <ImageList {...{ element, showEditModal, state, setState, onChange, index, disabled }} />
        <AlignmentControls
          align={state.align}
          direction={state.direction}
          disabled={!element.is_editable_value || disabled}
          justify={state.justify}
          onAlignChange={handleAlignChange}
          onDirectionChange={handleDirectionChange}
          onJustifyChange={handleJustifyChange}
        />
      </Form.Item>
      {imageModifyState.modalOpen ? (
        <ModalEditImage
          handleCancel={handleCancel}
          imageModifyState={imageModifyState}
          onSave={handleSave}
          onSaveACopy={handleSaveACopy}
        />
      ) : null}
    </Fragment>
  );
};

export default ImageInput;

const ImagePreview = memo(
  ({
    img,
    index,
    onEdit,
    onDelete,
    isEditable,
  }: {
    img: ImageInputProps['element']['images'][0];
    index: number;
    onEdit: (index: number) => void;
    onDelete: (index: number) => void;
    isEditable: boolean;
  }) => (
    <div
      key={img.id}
      className="relative"
      style={{
        position: 'relative',
        flex: '0 0 auto',
        width: '100px',
        height: '100px',
      }}
    >
      <img
        alt={`Upload ${index + 1}`}
        crossOrigin="anonymous"
        src={img.src}
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      />
      {isEditable && (
        <div
          className="overlay text-white"
          style={{
            width: '100%',
            height: '100%',
            position: 'absolute',
            display: 'flex',
            background: 'rgba(0,0,0,0.5)',
            justifyContent: 'center',
            alignItems: 'center',
            fontSize: 16,
            transition: 'all 0.3s',
            top: 0,
            left: 0,
          }}
        >
          <Space size="middle">
            <EditOutlined
              className="image-action"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onEdit(index);
              }}
            />
            <DeleteOutlined
              className="image-action"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onDelete(index);
              }}
            />
          </Space>
        </div>
      )}
    </div>
  )
);
ImagePreview.displayName = 'ImagePreview';

const ModalEditImage = memo(
  ({
    imageModifyState,
    handleCancel,
    onSaveACopy,
    onSave,
  }: {
    imageModifyState: ImageModifyState;
    handleCancel: () => void;
    onSaveACopy: (image, imageProps) => void;
    onSave: (image, imageProps) => void;
  }) => {
    const { t } = useTranslation('Vindoc');

    return (
      <Modal
        footer={null}
        open={imageModifyState.modalOpen}
        title={t('Edit image')}
        width={800}
        zIndex={2000}
        onCancel={handleCancel}
      >
        <MedicalImageEdit
          imageProps={imageModifyState.image}
          onCancel={handleCancel}
          onSave={onSave}
          onSaveACopy={onSaveACopy}
        />
      </Modal>
    );
  }
);
ModalEditImage.displayName = 'ModalEditImage';

const AlignmentControls = memo(
  ({
    direction,
    justify,
    align,
    onDirectionChange,
    onJustifyChange,
    onAlignChange,
    disabled,
  }: {
    direction: State['direction'];
    justify: State['justify'];
    align: State['align'];
    onDirectionChange: (e: RadioChangeEvent) => void;
    onJustifyChange: (e: RadioChangeEvent) => void;
    onAlignChange: (e: RadioChangeEvent) => void;
    disabled: boolean;
  }) => {
    const { t } = useTranslation('Vindoc');

    return (
      <>
        <Radio.Group
          className="mb-2"
          disabled={disabled}
          style={{ marginTop: '16px' }}
          value={direction}
          onChange={onDirectionChange}
        >
          <Radio value="row">{t('Horizontal')}</Radio>
          <Radio value="column">{t('Vertical')}</Radio>
        </Radio.Group>
        <Radio.Group
          className="mb-2"
          disabled={disabled}
          style={{ marginTop: '16px' }}
          value={justify}
          onChange={onJustifyChange}
        >
          <Tooltip title={t('Align left')}>
            <Radio.Button value="start">
              <FormatAlignLeftOutlined className="text-xl text-[white]" />
            </Radio.Button>
          </Tooltip>
          <Tooltip title={t('Align center')}>
            <Radio.Button value="center">
              <FormatAlignCenterOutlined className="text-xl text-[white]" />
            </Radio.Button>
          </Tooltip>
          <Tooltip title={t('Align right')}>
            <Radio.Button value="end">
              <FormatAlignRightOutlined className="text-xl text-[white]" />
            </Radio.Button>
          </Tooltip>
        </Radio.Group>
        <Radio.Group
          className="mb-2"
          disabled={disabled}
          style={{ marginTop: '16px' }}
          value={align}
          onChange={onAlignChange}
        >
          <Tooltip title={t('Align top')}>
            <Radio.Button value="start">
              <VerticalAlignTopOutlined className="text-xl text-[white]" />
            </Radio.Button>
          </Tooltip>
          <Tooltip title={t('Center vertically')}>
            <Radio.Button value="center">
              <VerticalAlignCenterOutlined className="text-xl text-[white]" />
            </Radio.Button>
          </Tooltip>
          <Tooltip title={t('Align bottom')}>
            <Radio.Button value="end">
              <VerticalAlignBottomOutlined className="text-xl text-[white]" />
            </Radio.Button>
          </Tooltip>
        </Radio.Group>
      </>
    );
  },
  (prevProps, nextProps) =>
    prevProps.direction === nextProps.direction &&
    prevProps.justify === nextProps.justify &&
    prevProps.align === nextProps.align &&
    prevProps.disabled === nextProps.disabled
);
AlignmentControls.displayName = 'AlignmentControls';

const readFileAsDataURL = (file: RcFile): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('FileReader result is not a string'));
      }
    };

    reader.onerror = () => {
      reject(new Error(`FileReader error: ${reader.error}`));
    };
  });
};

const loadImage = (src: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => resolve(img);
    img.onerror = (err) => reject(new Error(`Image load error: ${err}`));
    img.src = src;
  });
};

const generateFileKey = (file: RcFile): string => {
  return `${file.name}-${file.size}-${file.lastModified}-${uuidv4()}`;
};

const ImageList = ({
  element,
  showEditModal,
  state,
  setState,
  onChange,
  index,
  disabled,
}: Pick<ImageInputProps, 'disabled' | 'onChange' | 'index'> & {
  element: ImageElement;
  state: State;
  setState: Dispatch<SetStateAction<State>>;
  showEditModal: (index: number) => void;
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const imageUploadedListRef = useRef<Map<string, ImageData>>(new Map());

  const handleMultipleImageUpload = useCallback(
    async (files: RcFile[]) => {
      setIsUploading(true);
      try {
        // Process all files concurrently
        const newImages: ImageData[] = await Promise.all(
          files.map(async (file) => {
            const fileKey = generateFileKey(file);

            if (imageUploadedListRef.current.has(fileKey)) {
              console.log(`Reusing uploaded image for file: ${file.name}`);
              return imageUploadedListRef.current.get(fileKey);
            }

            // Step 1: Read file as Data URL
            const dataURL = await readFileAsDataURL(file);

            // Step 2: Load image to get dimensions
            const img = await loadImage(dataURL);
            const ratio = img.width / img.height;

            // Step 3: Get presigned upload URL
            const response = await getURLUploadImageElement({
              action: 0,
              file_names: [file.name],
            });

            const presignedData = response?.data?.presigned_objects?.[0];
            if (!presignedData || !presignedData.url || !presignedData.id) {
              throw new Error('Invalid upload URL response');
            }

            // Step 4: Upload image to the presigned URL
            await uploadImageElement(presignedData.url, file);

            // Step 5: Construct the ImageData object
            const newImage: ImageData = {
              id: presignedData.id,
              src: dataURL,
              width: element.default_width,
              height: element.default_height,
              ratio,
              manualAdjustment: true,
            };

            imageUploadedListRef.current.set(fileKey, newImage);

            return newImage;
          })
        );

        // Step 6: Update state with the new images
        setState((prev) => {
          const updatedImages = [...prev.images, ...newImages];
          onChange({ images: updatedImages }, element);
          return { ...prev, images: updatedImages };
        });
      } catch (error) {
        console.error('Error uploading multiple images:', error);
        // Optionally, you can add user-facing error handling here
      } finally {
        setIsUploading(false);
      }
    },
    [element, onChange]
  );

  const handleRemoveImage = useCallback(
    (index: number) => {
      const newImages = [...state.images];
      newImages.splice(index, 1);
      setState((prev) => ({ ...prev, images: newImages }));
      onChange({ images: newImages }, element);
    },
    [element, state.images]
  );

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'nowrap' }}>
      <UploadBox
        multiple
        className="min-w-[102px]"
        disabled={!element.is_editable_value || disabled}
        loading={isUploading}
        onFilesSelected={handleMultipleImageUpload}
      />
      <div
        className="image-previews"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
          alignItems: 'flex-start',
        }}
      >
        {(state.images || []).map((img, idx) => (
          <ImagePreview
            key={index}
            img={img}
            index={idx}
            isEditable={element.is_editable_value && !disabled}
            onDelete={handleRemoveImage}
            onEdit={showEditModal}
          />
        ))}
      </div>
    </div>
  );
};

// export default memo(
//   ImageInput,
//   (prev, next) => true
//     // isEqual(prev.element.images, next.element.images) &&
//     // isEqual(prev.element.default_image, next.element.default_image) &&
//     // prev.element.is_hidden === next.element.is_hidden &&
//     // prev.element.is_editable_value === next.element.is_editable_value &&
//     // prev.element.default_width === next.element.default_width &&
//     // prev.element.default_height === next.element.default_height &&
//     // prev.element.align === next.element.align &&
//     // prev.element.justify === next.element.justify &&
//     // prev.element.direction === next.element.direction
//     // isEqual(prev.element.image_ids, next.element.image_ids)
// );
